import { useState, useMemo, useRef, useEffect } from "react";
import { Search, Check, ChevronDown, X, Package } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

/**
 * Select ingredienti con ricerca, raggruppato per categoria
 * Ottimizzato per mobile
 */
const IngredienteSearchSelect = ({ 
  ingredienti = [], 
  categorie = [],
  value, 
  onChange, 
  placeholder = "Cerca ingrediente...",
  hasError = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Chiudi dropdown quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus su input quando si apre
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Ingrediente selezionato
  const selectedIngrediente = useMemo(() => 
    ingredienti.find(i => i.id === value), 
    [ingredienti, value]
  );

  // Ordina e raggruppa ingredienti
  const ingredientiRaggruppati = useMemo(() => {
    // Filtra per ricerca
    const searchLower = search.toLowerCase().trim();
    let filtered = ingredienti;
    
    if (searchLower) {
      filtered = ingredienti.filter(ing => 
        ing.nome.toLowerCase().includes(searchLower) ||
        (ing.categoria_nome || "").toLowerCase().includes(searchLower)
      );
    }

    // Ordina per categoria e poi per nome
    const sorted = [...filtered].sort((a, b) => {
      const catA = (a.categoria_nome || "Altro").toLowerCase();
      const catB = (b.categoria_nome || "Altro").toLowerCase();
      if (catA !== catB) return catA.localeCompare(catB, 'it');
      return a.nome.toLowerCase().localeCompare(b.nome.toLowerCase(), 'it');
    });

    // Raggruppa per categoria
    const grouped = {};
    sorted.forEach(ing => {
      const cat = ing.categoria_nome || "Altro";
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(ing);
    });

    return grouped;
  }, [ingredienti, search]);

  const handleSelect = (ing) => {
    onChange(ing.id);
    setIsOpen(false);
    setSearch("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
    setSearch("");
  };

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full flex items-center justify-between gap-2 px-3 py-2 
          text-left text-sm rounded-lg border bg-background
          transition-colors min-h-[40px]
          ${hasError ? 'border-red-500' : 'border-input hover:border-primary/50'}
          ${isOpen ? 'ring-2 ring-primary/20' : ''}
        `}
      >
        {selectedIngrediente ? (
          <div className="flex-1 flex items-center gap-2 min-w-0">
            <span className="truncate font-medium">{selectedIngrediente.nome}</span>
            <span className="text-muted-foreground text-xs flex-shrink-0">
              €{(selectedIngrediente.prezzo_attuale || 0).toFixed(2)}/{selectedIngrediente.unita_misura}
            </span>
          </div>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
        <div className="flex items-center gap-1 flex-shrink-0">
          {selectedIngrediente && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-muted rounded"
            >
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          )}
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-lg overflow-hidden">
          {/* Search Input - Sticky */}
          <div className="p-2 border-b border-border bg-muted/30 sticky top-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={inputRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca per nome o categoria..."
                className="pl-9 h-10 text-base"
                autoComplete="off"
              />
            </div>
          </div>

          {/* Lista ingredienti raggruppati */}
          <div className="max-h-[50vh] overflow-y-auto overscroll-contain">
            {Object.keys(ingredientiRaggruppati).length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nessun ingrediente trovato</p>
              </div>
            ) : (
              Object.entries(ingredientiRaggruppati).map(([categoria, items]) => (
                <div key={categoria}>
                  {/* Header categoria */}
                  <div className="px-3 py-2 bg-muted/50 text-xs font-semibold text-muted-foreground uppercase tracking-wide sticky top-0">
                    {categoria} ({items.length})
                  </div>
                  {/* Items */}
                  {items.map(ing => {
                    const isSelected = ing.id === value;
                    return (
                      <button
                        key={ing.id}
                        type="button"
                        onClick={() => handleSelect(ing)}
                        className={`
                          w-full flex items-center gap-3 px-3 py-3 text-left
                          transition-colors border-b border-border/50 last:border-0
                          ${isSelected 
                            ? 'bg-primary/10 text-primary' 
                            : 'hover:bg-muted/50 active:bg-muted'
                          }
                        `}
                      >
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium truncate ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                            {ing.nome}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            €{(ing.prezzo_attuale || 0).toFixed(2)}/{ing.unita_misura}
                          </p>
                        </div>
                        {isSelected && (
                          <Check className="h-5 w-5 text-primary flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IngredienteSearchSelect;
